import "@testing-library/jest-dom";
import { fireEvent, render, screen, within } from "@testing-library/react";
import { CanvasDataWidgetRenderer } from "./CanvasDataWidgetRenderer";
import { CanvasCalendarRenderer } from "./CanvasCalendarRenderer";
import { useViewDataQuery } from "../../../hooks/useViewDataQuery";
import { openCanvasNavigation } from "../canvasNavigation";

jest.mock("../../../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({ workspace: { id: "ws-1" } }),
}));
jest.mock("../../../hooks/useViewDataQuery", () => ({
  useViewDataQuery: jest.fn(),
}));
jest.mock("../canvasNavigation", () => ({ openCanvasNavigation: jest.fn() }));
jest.mock("react-router-dom", () => ({
  useNavigate: () => jest.fn(),
  useLocation: () => ({ pathname: "/calendar", search: "" }),
}));
jest.mock("../../../../../utils/useAuthFetch", () => ({
  useAuthFetch: () => jest.fn(),
}));
jest.mock("../CanvasRuntimeContext", () => ({
  useCanvasRuntime: () => ({
    canvasId: "canvas-1",
    workspaceId: "ws-1",
    rowId: "row-9",
    interactionBus: { getRuntimeFilters: () => [] },
  }),
}));

const query = useViewDataQuery as jest.Mock;
const navigateAction = {
  type: "NAVIGATE",
  config: { target_canvas_id: "detail-canvas", record_source: "selected" },
};
const element = (overrides: Record<string, unknown> = {}) => ({
  id: "calendar-1",
  type: "calendar",
  gridColumn: 1,
  gridRow: 1,
  columnSpan: 12,
  rowSpan: 6,
  props: {
    title: "Interviews",
    data_source_alias: "events",
    calendar_config: {
      date_field: "starts_on",
      end_date_field: "ends_on",
      title_field: "name",
      ...overrides,
    },
  },
});
const dataSources = [
  {
    id: "events",
    name: "events",
    type: "view" as const,
    view_id: 5,
    config: {},
  },
];
const rows = [
  { _rowId: "e1", name: "Interview Ada", starts_on: "2026-10-14" },
  {
    _rowId: "e2",
    name: "Offsite",
    starts_on: "2026-10-20",
    ends_on: "2026-10-22T17:00:00Z",
  },
  { _rowId: "e3", name: "Planning", starts_on: "2026-11-03T09:30:00" },
  { _rowId: "e4", name: "No date yet", starts_on: null },
  { _rowId: "e5", name: "Garbled", starts_on: "next tuesday" },
];

const renderCalendar = (props = element()) =>
  render(
    <CanvasCalendarRenderer
      element={props as never}
      dataSources={dataSources}
      canvasId="canvas-1"
    />,
  );

beforeEach(() => {
  jest.useFakeTimers();
  jest.setSystemTime(new Date(2026, 9, 15, 12, 0, 0));
  query.mockReset();
  query.mockReturnValue({
    data: { rows, totalCount: rows.length },
    isLoading: false,
    error: null,
  });
  (openCanvasNavigation as jest.Mock).mockReset();
  (openCanvasNavigation as jest.Mock).mockResolvedValue(undefined);
});
afterEach(() => jest.useRealTimers());

describe("CanvasCalendarRenderer", () => {
  it("draws the current month with each event on its day and a multi-day event on every day it covers", () => {
    renderCalendar();
    expect(screen.getByText("October 2026")).toBeInTheDocument();
    expect(
      within(screen.getByTestId("day-2026-10-14")).getByText("Interview Ada"),
    ).toBeInTheDocument();
    for (const day of ["20", "21", "22"]) {
      expect(
        within(screen.getByTestId(`day-2026-10-${day}`)).getByText("Offsite"),
      ).toBeInTheDocument();
    }
    expect(
      within(screen.getByTestId("day-2026-10-23")).queryByText("Offsite"),
    ).toBeNull();
    // the month starts on a Monday-first grid
    expect(screen.getAllByRole("columnheader")[0]).toHaveTextContent("Mon");
  });

  it("says how many rows have no usable date instead of dropping them silently", () => {
    renderCalendar();
    expect(
      screen.getByText("2 events without a usable date are not shown"),
    ).toBeInTheDocument();
  });

  it("moves between months and back to today", () => {
    renderCalendar();
    fireEvent.click(screen.getByRole("button", { name: "Next" }));
    expect(screen.getByText("November 2026")).toBeInTheDocument();
    expect(
      within(screen.getByTestId("day-2026-11-03")).getByText("Planning"),
    ).toBeInTheDocument();
    fireEvent.click(screen.getByRole("button", { name: "Today" }));
    expect(screen.getByText("October 2026")).toBeInTheDocument();
  });

  it("switches to a Monday-first week and honours a week default view", () => {
    renderCalendar();
    fireEvent.click(screen.getByRole("button", { name: "Week" }));
    expect(screen.getByText("12 Oct - 18 Oct 2026")).toBeInTheDocument();
    expect(screen.getAllByTestId(/^day-/)).toHaveLength(7);
    expect(
      within(screen.getByTestId("day-2026-10-14")).getByText("Interview Ada"),
    ).toBeInTheDocument();
    fireEvent.click(screen.getByRole("button", { name: "Next" }));
    expect(screen.getByText("19 Oct - 25 Oct 2026")).toBeInTheDocument();
    expect(
      within(screen.getByTestId("day-2026-10-21")).getByText("Offsite"),
    ).toBeInTheDocument();
  });

  it("starts in week view when the config says so", () => {
    renderCalendar(element({ default_view: "week" }));
    expect(screen.getByText("12 Oct - 18 Oct 2026")).toBeInTheDocument();
  });

  it("uses the start date for an event without an end date field", () => {
    renderCalendar(element({ end_date_field: undefined }));
    expect(
      within(screen.getByTestId("day-2026-10-20")).getByText("Offsite"),
    ).toBeInTheDocument();
    expect(
      within(screen.getByTestId("day-2026-10-21")).queryByText("Offsite"),
    ).toBeNull();
  });

  it("opens the configured detail canvas for the clicked event's record", () => {
    renderCalendar(element({ event_click_action: navigateAction }));
    fireEvent.click(screen.getByText("Interview Ada"));
    expect(openCanvasNavigation).toHaveBeenCalledWith(
      navigateAction.config,
      expect.objectContaining({
        workspaceId: "ws-1",
        currentRowId: "row-9",
        selectedRowId: "e1",
      }),
    );
  });

  it("does nothing on an event click when no click-through is configured", () => {
    renderCalendar();
    fireEvent.click(screen.getByText("Interview Ada"));
    expect(openCanvasNavigation).not.toHaveBeenCalled();
  });

  it("pages in every row", () => {
    const make = (from: number, count: number) =>
      Array.from({ length: count }, (_, index) => ({
        _rowId: `p${from + index}`,
        name: `Event ${from + index}`,
        starts_on: "2026-10-01",
      }));
    query.mockImplementation((_viewId: string, params: { page?: number }) => ({
      data:
        (params.page ?? 1) === 1
          ? { rows: make(1, 100), totalCount: 120 }
          : { rows: make(101, 20), totalCount: 120 },
      isLoading: false,
      error: null,
    }));
    renderCalendar();
    expect(query).toHaveBeenCalledWith(
      "5",
      expect.objectContaining({ page: 2, pageSize: 100 }),
    );
  });

  it("shows loading and error states and asks for a mapping when unbound", () => {
    query.mockReturnValueOnce({ isLoading: true, error: null });
    const { rerender } = renderCalendar();
    expect(screen.getByText("Loading calendar…")).toBeInTheDocument();
    query.mockReturnValueOnce({ isLoading: false, error: new Error("no") });
    rerender(
      <CanvasCalendarRenderer
        element={element() as never}
        dataSources={dataSources}
        canvasId="canvas-1"
      />,
    );
    expect(screen.getByText("Unable to load calendar.")).toBeInTheDocument();
    rerender(
      <CanvasCalendarRenderer
        element={
          {
            ...element(),
            props: { title: "Cal", data_source_alias: "events" },
          } as never
        }
        dataSources={dataSources}
        canvasId="canvas-1"
      />,
    );
    expect(
      screen.getByText(
        "Configure a view, date field and title field for this calendar.",
      ),
    ).toBeInTheDocument();
  });
});

it("draws a bound calendar through the runtime data-widget dispatcher", () => {
  render(
    <CanvasDataWidgetRenderer
      element={element() as never}
      dataSources={dataSources}
      canvasId="canvas-1"
    />,
  );
  expect(screen.getByText("October 2026")).toBeInTheDocument();
  expect(
    within(screen.getByTestId("day-2026-10-14")).getByText("Interview Ada"),
  ).toBeInTheDocument();
  expect(query).toHaveBeenCalledWith(
    "5",
    expect.objectContaining({ canvasId: "canvas-1" }),
  );
});
